import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { MarketingLayout, PageHero, useHydratedTranslation } from "@/components/marketing/Layout";
import { useState } from "react";
import { ChevronDown } from "lucide-react";

type FaqGroup = { title: string; items: { q: string; a: string }[] };

// SEO-driven FAQ targeting high-intent queries surfaced by Semrush:
// "what is an ai twin", "how to create an ai twin", "ai chatbot for business",
// "best ai chatbot for small business", "botpress / chatbase alternative",
// plus onboarding, pricing, privacy and data-residency questions we get in sales.
const FAQ_GROUPS: { title: string; items: { q: string; a: string }[] }[] = [
  {
    title: "AI Twins — the basics",
    items: [
      {
        q: "What is an AI Twin?",
        a: "An AI Twin is a conversational digital version of your company. It's trained on your own website, documents and product truth, and can answer visitors, qualify leads and route serious opportunities to your team — 24/7, in your voice, with citations back to the source.",
      },
      {
        q: "How is an AI Twin different from a chatbot?",
        a: "A traditional chatbot follows scripted flows or generic LLM answers. A Reality Twin is grounded in your knowledge graph — every reply is cited, memory is persistent per visitor, and the same twin can meet other twins in the network to broker introductions. See our comparisons: /vs-chatbase and /vs-botpress.",
      },
      {
        q: "How is an AI Twin different from a digital twin?",
        a: "Industrial digital twins model physical assets (a turbine, a factory). An AI Twin models a company's knowledge, offer and voice so it can hold real conversations with customers, partners and investors.",
      },
      {
        q: "How do I create an AI Twin?",
        a: "Sign up, paste your website URL, and Reality Twin crawls it, builds a citable knowledge graph and spins up your first twin — usually in about 60 seconds. You can then upload extra docs, tune the personality and embed the widget.",
      },
      {
        q: "Do I need to code to launch a twin?",
        a: "No. The setup and the embeddable widget are no-code. Developers can also drive everything via the REST API and (soon) the TypeScript SDK.",
      },
    ],
  },
  {
    title: "For business — sales, support, marketing",
    items: [
      {
        q: "What's the best AI chatbot for a small business?",
        a: "For small teams the trade-off is honest onboarding vs. deep configuration. Reality Twin is designed for founders and small teams: it self-installs from your website, captures and scores leads out of the box, and doesn't require an engineer to maintain. Free plan lets you validate before paying.",
      },
      {
        q: "Can an AI Twin qualify leads for my sales team?",
        a: "Yes. Every conversation is scored on intent and fit. Hot leads land in your inbox already enriched with company info, use case and next step, and can be pushed into HubSpot or Salesforce on Pro and above.",
      },
      {
        q: "Can the twin book meetings or appointments?",
        a: "Yes — on Starter and above you can connect a calendar and let the twin propose and confirm slots inside the conversation.",
      },
      {
        q: "Which channels can I deploy an AI Twin on?",
        a: "Today: your website (embeddable widget or full page), your marketplace profile, and the REST API. Slack, WhatsApp and email are on the roadmap.",
      },
      {
        q: "Does it work in languages other than English?",
        a: "Yes. Reality Twin is multilingual — the platform UI ships in 7 languages (EN, DE, ES, FR, IT, RU, ZH) and twins answer in whatever language the visitor writes in.",
      },
    ],
  },
  {
    title: "Knowledge, accuracy & hallucinations",
    items: [
      {
        q: "How do you prevent the twin from making things up?",
        a: "Every answer is grounded in your knowledge graph and cited back to the source document or page. If the twin can't find a citation, it says so and offers to route the question to a human — hallucinations are treated as bugs, not features.",
      },
      {
        q: "What knowledge sources can I connect?",
        a: "Your website (crawled automatically), uploaded documents (PDF, DOCX, MD, TXT), Notion pages, and structured Q&A pairs. Free plan covers your website; Starter adds up to 10 docs; Pro is unlimited.",
      },
      {
        q: "How often does the twin re-crawl my website?",
        a: "Weekly by default; you can trigger a manual recrawl anytime, or use the API/webhook to push updates the moment your CMS publishes.",
      },
      {
        q: "Can I control the twin's personality and tone?",
        a: "Yes. You define voice (formal → playful), verbosity, refusal behaviour and topic boundaries. Changes go live immediately and can be A/B tested per channel.",
      },
    ],
  },
  {
    title: "Pricing & plans",
    items: [
      {
        q: "Is there a free plan?",
        a: "Yes. Free includes 100 conversations/month, 1 AI Twin, a public marketplace listing and community support. No credit card required — see /pricing.",
      },
      {
        q: "How much does an AI Twin cost?",
        a: "Starter is $29/mo (2,500 conversations, 3 Twins, lead capture). Pro is $99/mo (25,000 conversations, unlimited Twins, Twin-to-Twin protocol). Enterprise is custom with SSO, private models and regional hosting.",
      },
      {
        q: "What counts as a conversation?",
        a: "A conversation is a single visitor session with the twin, regardless of message count. Sessions expire after 30 minutes of inactivity.",
      },
      {
        q: "Can I upgrade, downgrade or cancel anytime?",
        a: "Yes — plan changes take effect immediately and are prorated. Cancel anytime from the billing screen; your data stays available for export for 30 days.",
      },
    ],
  },
  {
    title: "Privacy, data & hosting",
    items: [
      {
        q: "Where is my data stored?",
        a: "Application data lives in the European Union (Ireland). Reality Twin is a Swiss company under Swiss law — the company is Swiss, the storage is EU. Full details on /security and /dpa.",
      },
      {
        q: "Do you train shared models on my content?",
        a: "No. Your knowledge stays isolated to your workspace at the database row level with Row-Level Security. We never train shared foundation models on your content.",
      },
      {
        q: "Is Reality Twin GDPR-compliant?",
        a: "Yes — GDPR and Swiss FADP aligned. A signed DPA is available on request via hello@realitytwin.io.",
      },
      {
        q: "Who can access the twin's admin?",
        a: "Only accounts you invite to your workspace. All operator access is protected by MFA. Production access on our side is restricted to the founder and audit-logged.",
      },
    ],
  },
  {
    title: "Twin-to-Twin & the network",
    items: [
      {
        q: "What is the Twin-to-Twin protocol?",
        a: "When two twins in the network align on intent, capability and fit, Reality Twin proposes a meeting to both sides — no cold outreach. The protocol handles the discovery, the intent signals and the consent handshake.",
      },
      {
        q: "Is my company automatically listed in the marketplace?",
        a: "Only if you opt in. You control the public profile, what the twin will disclose to other twins, and which industries can discover you.",
      },
    ],
  },
  {
    title: "Cost, setup & getting started",
    items: [
      {
        q: "How much does an AI chatbot cost?",
        a: "It ranges from free to thousands per month. Reality Twin starts free (50 conversations/month), then CHF 39 (Starter, 1,000), CHF 149 (Pro, 5,000) and CHF 399 (Scale, 20,000). Conversations are a shared pool across all your twins and you can buy more any time.",
      },
      {
        q: "How do I add an AI chatbot to my website?",
        a: "Paste one script tag into your site and the twin appears as a chat widget — no code beyond that. You can also embed a full-page version or use the REST API and MCP server.",
      },
      {
        q: "Can I build an AI chatbot without coding?",
        a: "Yes. Setup, knowledge, personality and the embed widget are all no-code. Developers can additionally use the REST API and MCP server for deeper control.",
      },
      {
        q: "Is an AI chatbot worth it for a small business?",
        a: "For most small businesses, yes: it answers repetitive questions 24/7, captures leads you'd otherwise miss overnight, and needs no extra headcount. The free tier lets you validate before paying.",
      },
    ],
  },
  {
    title: "Comparisons, languages & compliance",
    items: [
      {
        q: "Is Reality Twin a good Chatbase or Botpress alternative?",
        a: "For B2B teams that care about pipeline rather than deflection, yes. It adds grounding with citations, real lead qualification and CRM write-back, and Twin-to-Twin discovery. See /vs-chatbase and /vs-botpress for detailed comparisons.",
      },
      {
        q: "Does the AI chatbot support multiple languages?",
        a: "Yes. The interface ships in 7 languages and twins answer in whatever language the visitor writes in, so one twin serves a global audience.",
      },
      {
        q: "Is an AI chatbot GDPR compliant?",
        a: "Reality Twin is aligned with the GDPR and the Swiss FADP. Application data lives in the European Union (Ireland), your knowledge is isolated per workspace with row-level security, and a signed DPA is available on request.",
      },
      {
        q: "Will AI replace my support or sales team?",
        a: "No — it removes the repetitive first layer so your team spends time on conversations that need judgment. The strongest setups pair an AI Twin for instant 24/7 answers with humans for complex, high-value cases.",
      },
    ],
  },
];

export const Route = createFileRoute("/faq")({
  head: () => {
    const seo = buildSeo({
      path: "/faq",
      title: "AI Twin FAQ — Reality Twin pricing & privacy",
      description:
        "Answers to the most common questions about AI Twins, Reality Twin's platform, pricing, data privacy, hosting and the Twin-to-Twin network.",
    });
    const faqEntities = FAQ_GROUPS.flatMap((g) => g.items).map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    }));
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqEntities,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://realitytwin.io/" },
              { "@type": "ListItem", position: 2, name: "FAQ", item: "https://realitytwin.io/faq" },
            ],
          }),
        },
      ],
    };
  },
  component: FaqPage,
});

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 py-5 text-left"
        aria-expanded={open}
      >
        <h3 className="text-[16px] font-medium leading-snug text-foreground">{q}</h3>
        <ChevronDown
          className={`h-4 w-4 mt-1 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>
      {open && (
        <div className="pb-6 pr-10 text-[14.5px] leading-[1.7] text-muted-foreground">{a}</div>
      )}
    </div>
  );
}

function FaqPage() {
  const t = useHydratedTranslation();
  // Visible content is localized; the FAQ_GROUPS const above stays English
  // only to feed the FAQPage JSON-LD (targets English search queries).
  const groups = t("routes.faq.groups", { returnObjects: true }) as unknown as FaqGroup[];
  return (
    <MarketingLayout>
      <PageHero
        eyebrow={t("routes.faq.eyebrow")}
        title={
          <>
            {t("routes.faq.titlePre")}
            <em className="font-serif italic">{t("routes.faq.titleEm")}</em>
            {t("routes.faq.titlePost")}
          </>
        }
        sub={t("routes.faq.sub")}
      />

      <section className="py-16">
        <div className="mx-auto max-w-3xl px-6 space-y-14">
          {groups.map((group) => (
            <div key={group.title}>
              <div className="font-mono-eyebrow mb-3">{group.title}</div>
              <div>
                {group.items.map((it) => (
                  <FaqItem key={it.q} q={it.q} a={it.a} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-cream border-y border-border py-20">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-[36px] leading-tight tracking-[-0.025em]">
            {t("routes.faq.stillTitle")}
          </h2>
          <p className="mt-4 text-[15px] text-muted-foreground">
            {t("routes.faq.stillPre")}
            <Link to="/support" className="underline text-foreground">
              {t("routes.faq.stillSupportLink")}
            </Link>
            {t("routes.faq.stillMid")}
            <a href="mailto:hello@realitytwin.io" className="underline text-foreground">
              hello@realitytwin.io
            </a>
            {t("routes.faq.stillPost")}
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
